import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { apiKeyState } from '../lib/apiKeyState.js';

const ROLE_LABEL = {
  practice_admin: 'Administrator',
  practice_staff: 'Bookkeeper',
  read_only: 'Read only',
  client_admin: 'Client admin',
  client_user: 'Client user',
};
const ROLES = Object.keys(ROLE_LABEL);

const fmt = (iso) => (iso ? iso.slice(0, 16).replace('T', ' ') : null);

/** Colour a key's state the same way the rest of the ledger colours a fact
 * worth flagging: red for something wrong or dead, faint for something to
 * notice, plain ink for the ordinary case. */
const STATE_STYLE = {
  revoked: { className: 'neg' },
  expired: { className: 'neg' },
  'never-used': { style: { color: 'var(--ink-faint)' } },
};

/**
 * Mint, inspect and revoke API keys — the credentials machines and
 * integrations use, never a person signing in.
 *
 * Gated by the caller (see Shell.jsx / App.jsx) on the `keys.manage`
 * capability from /api/auth/me, not on a role string, and the server checks
 * again on every request this page makes — hiding the link is not access
 * control, only the avoidance of a door that will not open.
 */
export function ApiKeys({ entities }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState({ name: '', roles: {}, expiresAt: '' });
  const [busy, setBusy] = useState(false);
  // The plaintext secret exists only here, only once, only for as long as the
  // administrator has not dismissed this panel — see acknowledge() below.
  const [minted, setMinted] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = () => api.get('/api/api-keys').then(setData).catch((e) => setErr(e.message));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const grants = Object.entries(form.roles)
        .filter(([, role]) => role)
        .map(([entityId, role]) => ({ entityId, role }));
      const body = { name: form.name, grants };
      if (form.expiresAt) {
        // The date input gives a calendar day; the server wants a full
        // instant, so the key is treated as live through the whole of the
        // day the administrator picked, not until midnight at its start.
        body.expiresAt = new Date(`${form.expiresAt}T23:59:59.999Z`).toISOString();
      }
      const made = await api.post('/api/api-keys', body);
      setMinted(made);
      setCopied(false);
      setForm({ name: '', roles: {}, expiresAt: '' });
      load();
    } catch (e) {
      // CANNOT_GRANT and NO_ENTITY already come back as plain English from
      // the server ("you cannot grant ...", "you have no access to that
      // company") — passed straight through rather than flattened to a
      // generic failure.
      setErr(e.message);
    } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    setErr(null); setMsg(null);
    try {
      await api.post(`/api/api-keys/${id}/revoke`);
      setMsg('Key revoked.');
      load();
    } catch (e) {
      if (e.status === 404) {
        // Re-revoking answers 404 — most likely a second administrator won
        // the race a moment earlier. Either way the key is dead, which is
        // what the click wanted, so this reads as done rather than as an
        // error.
        setMsg('That key was already revoked.');
        load();
      } else {
        setErr(e.message);
      }
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(minted.plaintext);
      setCopied(true);
    } catch {
      // No Clipboard API (or no permission): the field below is still
      // readOnly and select-on-focus, so the secret can be copied by hand.
    }
  };

  if (!data) return <div className="empty">Loading…</div>;

  const ready = form.name.trim().length > 1 && Object.values(form.roles).some(Boolean);

  return (
    <>
      <header className="rhead">
        <h1 className="rhead__title display">API keys</h1>
        <div className="rhead__sub">
          Credentials for machines and integrations — never for a person to sign in with.
        </div>
      </header>

      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      {minted && (
        <div className="note note--warn" style={{ marginBottom: 22 }}>
          <strong>
            This is the only time “{minted.key.name}” will show its secret. Copy it now —
            it will not be shown again, anywhere.
          </strong>
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <input readOnly value={minted.plaintext} onFocus={(e) => e.target.select()}
                   style={{ flex: 1, fontFamily: 'var(--figure)', fontSize: 12 }} />
            <button className="btn btn--quiet" style={{ padding: '5px 12px', fontSize: 11 }}
                    onClick={copy}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div style={{ marginTop: 10 }}>
            If you leave this page without copying it, the key cannot be recovered from
            here or from the server. Your only remedy will be to revoke it below and create
            a new one.
          </div>
          <button className="btn" style={{ marginTop: 12 }}
                  onClick={() => { setMinted(null); setCopied(false); }}>
            I have copied the key — close this
          </button>
        </div>
      )}

      <h2 className="eyebrow" style={{ margin: '26px 0 10px' }}>Create a key</h2>
      <div className="form-grid">
        <label className="field"><span>Name</span>
          <input value={form.name} placeholder="e.g. Zapier integration"
                 onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
        <label className="field"><span>Expires (optional)</span>
          <input type="date" value={form.expiresAt}
                 onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} /></label>
      </div>

      <div className="scroll-x">
        <table className="ledger">
          <thead><tr><th>Company</th><th style={{ width: 260 }}>Role</th></tr></thead>
          <tbody>
            {entities.map((e) => (
              <tr key={e.id}>
                <td>{e.name}</td>
                <td>
                  <select value={form.roles[e.id] ?? ''} style={{ width: '100%' }}
                          onChange={(ev) => setForm({
                            ...form, roles: { ...form.roles, [e.id]: ev.target.value } })}>
                    <option value="">No access</option>
                    {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="note">
        A key can only be granted a role you yourself hold on that company — asking for
        more will be refused.
      </div>

      <button className="btn" style={{ marginTop: 14 }} onClick={create} disabled={!ready || busy}>
        {busy ? 'Creating' : 'Create key'}
      </button>

      <h2 className="eyebrow" style={{ margin: '34px 0 10px' }}>Existing keys</h2>

      {data.keys.length === 0 ? (
        <div className="empty"><p>No API keys yet.</p></div>
      ) : (
        <div className="scroll-x">
          <table className="ledger">
            <thead>
              <tr>
                <th>Name</th><th>Prefix</th><th>Access</th><th>Created by</th>
                <th>Created</th><th>Last used</th><th>State</th><th style={{ width: 90 }} />
              </tr>
            </thead>
            <tbody>
              {data.keys.map((k) => {
                const s = apiKeyState(k);
                const decor = STATE_STYLE[s.state] ?? {};
                return (
                  <tr key={k.id}>
                    <td>{k.name}</td>
                    <td className="code figure">{k.prefix}</td>
                    <td>
                      {k.grants.length === 0
                        ? <span style={{ color: 'var(--ink-faint)' }}>No access</span>
                        : k.grants.map((g) => (
                            <div key={g.entityId} style={{ fontSize: 12 }}>
                              {g.entityName} — <span style={{ color: 'var(--ink-soft)' }}>
                                {ROLE_LABEL[g.role] ?? g.role}
                              </span>
                            </div>
                          ))}
                    </td>
                    <td style={{ fontSize: 12 }}>{k.createdBy}</td>
                    <td className="figure" style={{ fontSize: 12 }}>{fmt(k.createdAt)}</td>
                    <td className="figure" style={{ fontSize: 12 }}>
                      {/* Never-used is carried by the state column, not buried
                          here as a bare "never" — this column stays a date. */}
                      {k.lastUsedAt ? fmt(k.lastUsedAt) : '—'}
                    </td>
                    <td>
                      <span className={decor.className} style={decor.style}>{s.label}</span>
                    </td>
                    <td className="num">
                      {!k.revokedAt && (
                        <button className="icon-btn" onClick={() => revoke(k.id)}>Revoke</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
